import styled from 'styled-components';

import { ANIM, COLORS, MOBILE, SPACE } from '@/nova/theme/tokens';
import { Text } from '@/nova/components';

/** Shared form styled-components for the affiliate + campaign sheets (page-local). */

export const FormCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const GroupLabel = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.heading};
`;

export const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 4px;
`;

export const SegChips = styled.div`
  display: inline-flex;
  gap: 4px;
`;

export const SegChip = styled.button<{ $selected: boolean }>`
  height: 34px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$selected ? COLORS.accent : COLORS.surfaceMuted)};
  border: 1px solid ${(p) => (p.$selected ? COLORS.accent : COLORS.panelBorder)};
  color: ${(p) => (p.$selected ? COLORS.onAccent : COLORS.textMuted)};
`;

export const Helper = styled(Text)`
  font-size: 12px;
  line-height: 1.4;
  color: ${COLORS.textMuted};
`;

/** Start and end date side by side (stacked on a phone). */
export const DateRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${SPACE[12]};

  ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

/** A labelled native date input in the shared Field's look (the shared Field is text-only). */
export const DateLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;
`;

export const DateLabelText = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

export const DateInput = styled.input<{ $invalid?: boolean }>`
  width: 100%;
  min-width: 0;
  padding: 14px ${SPACE[16]};
  border-radius: 12px;
  border: 1px solid ${(p) => (p.$invalid ? COLORS.statusDanger : COLORS.panelBorder)};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-size: 16px;
  font-family: inherit;
  outline: none;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  /* The browser's outline is dropped, so the border says it has the focus. An invalid range stays red. */
  &:focus {
    border-color: ${(p) => (p.$invalid ? COLORS.statusDanger : COLORS.accent)};
  }

  &::-webkit-calendar-picker-indicator {
    filter: invert(0.7);
    cursor: pointer;
  }
`;

export const ErrorText = styled(Text)`
  font-size: 12px;
  color: ${COLORS.statusDanger};
`;

/** "More options": a quiet text toggle that reveals the rarely-needed fields. */
export const MoreToggle = styled.button<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: ${SPACE[4]};
  padding: ${SPACE[4]} 0;
  border: none;
  background: transparent;
  color: ${COLORS.textMuted};
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    color: ${COLORS.text};
  }

  & > svg {
    transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
    transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }
`;
